import { useEffect, useState } from 'react';
import { useGetAccountInfo } from '@multiversx/sdk-dapp/hooks';
import { getNFTs } from 'src/services/nftService.ts';
import { getCurrentEpoch } from 'src/services/statsService.ts';
import { getNFTRedeemValues } from 'src/types/nft.ts';
import NFTCard from 'src/components/NFTCard/NFTCard';
import style from './Rewards.module.scss';
import NFTTimelineDialog from 'src/components/NFTCard/NFTTimelineDialog';
import { Dialog } from '@mui/material';
import { environment, collectionId } from 'src/config';

function Rewards() {
    const { account } = useGetAccountInfo();
    const [nfts, setNfts] = useState([]);
    const [currentEpoch, setCurrentEpoch] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedNft, setSelectedNft] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [analyzeIdentifier, setAnalyzeIdentifier] = useState('');
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isExplorerView, setIsExplorerView] = useState(false);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            try {
                // Always fetch epoch
                const epoch = await getCurrentEpoch();
                setCurrentEpoch(epoch);
                console.log('Fetched epoch:', epoch);

                // Only fetch NFTs if we have an account
                if (account.address) {
                    const nftsResponse = await getNFTs(account.address);
                    console.log('Fetched NFTs:', nftsResponse.nfts);
                    setNfts(nftsResponse.nfts);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchData();
    }, [account.address]);

    const getExplorerUrl = (identifier) => {
        const prefix = environment === 'devnet' ? 'devnet-' : '';
        return `https://${prefix}explorer.multiversx.com/nfts/${identifier}`;
    };

    const handleNFTClick = (nft) => {
        setSelectedNft(nft);
        setIsExplorerView(false);
        setDialogOpen(true);
    };

    const handleAnalyzeNFT = async (e) => {
        if (e.key !== 'Enter') return;
        
        const cleanIdentifier = analyzeIdentifier.trim();
        if (!cleanIdentifier) return;

        console.log('Analyzing NFT with identifier:', cleanIdentifier);
        setIsLoading(true);
        
        try {
            const prefix = environment === 'devnet' ? 'devnet-' : '';
            const url = `https://${prefix}api.multiversx.com/nfts/${encodeURIComponent(cleanIdentifier)}`;
            console.log('Fetching NFT from:', url);
            
            const response = await fetch(url);
            console.log('API Response status:', response.status);
            
            const nft = await response.json();
            console.log('API Response data:', nft);

            if (!response.ok) {
                console.error('API error:', nft);
                throw new Error('NFT not found');
            }

            // Validate if it's from the rewards collection
            const REWARDS_COLLECTION = collectionId;
            console.log('Checking collection:', nft.collection, 'against', REWARDS_COLLECTION);
            if (nft.collection !== REWARDS_COLLECTION) {
                console.warn('Invalid collection:', nft.collection);
                setErrorMessage(`This NFT is not from the rewards collection (${REWARDS_COLLECTION}).`);
                setErrorDialogOpen(true);
                return;
            }

            console.log('Valid NFT found, showing timeline');
            setSelectedNft(nft);
            setIsExplorerView(true);
            setDialogOpen(true);
        } catch (error) {
            console.error('Error in handleAnalyzeNFT:', error);
            setErrorMessage(error.message === 'NFT not found' 
                ? 'NFT not found. Please check the identifier and try again.' 
                : 'An error occurred while fetching the NFT. Please try again.');
            setErrorDialogOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={style.rewards}>
            <div className={style.header}>
                <div className={style.content}>
                    <strong>Your rewards</strong>
                    <span>
                        Redeem those NFTs to receive the TADA in your wallet instantly. 
                        Check the liberation curve to see how much you'll obtain in a month.
                    </span>
                </div>
            </div>

            <div className={style.analyzeSection}>
                <div className={style.analyzeContainer}>
                    <div className={style.content}>
                        <strong>Analyze a NFT</strong>
                        <span>
                            Enter the NFT identifier to check its vesting schedule and redeemable amount.
                        </span>
                    </div>
                    <input
                        type="text"
                        placeholder="Enter NFT identifier"
                        className={style.analyzeInput}
                        value={analyzeIdentifier}
                        onChange={(e) => setAnalyzeIdentifier(e.target.value)}
                        onKeyDown={handleAnalyzeNFT}
                    />
                </div>
            </div>

            <div className={style.nftGrid}>
                {!account.address ? (
                    <div className={style.connectWallet}>
                        Connect your wallet to view your NFTs
                    </div>
                ) : isLoading ? (
                    <div className={style.loading}>Loading your NFTs...</div>
                ) : nfts.length > 0 ? (
                    nfts.map((nft) => (
                        <NFTCard 
                            key={nft.identifier} 
                            nft={nft} 
                            currentEpoch={currentEpoch}
                            onClick={() => handleNFTClick(nft)}
                        />
                    ))
                ) : (
                    <div className={style.noNfts}>No NFTs found in your wallet</div>
                )}
            </div>

            {selectedNft && (
                <NFTTimelineDialog 
                    open={dialogOpen}
                    onClose={() => {
                        setDialogOpen(false);
                        setSelectedNft(null);
                        setIsExplorerView(false);
                    }}
                    nft={selectedNft}
                    currentEpoch={currentEpoch}
                    values={getNFTRedeemValues(selectedNft, currentEpoch)}
                    is_explorer={isExplorerView}
                    explorerUrl={getExplorerUrl(selectedNft.identifier)}
                />
            )}

            <Dialog 
                open={errorDialogOpen} 
                onClose={() => setErrorDialogOpen(false)}
                PaperProps={{
                    style: {
                        backgroundColor: '#1E1E1E',
                        color: 'white',
                        padding: '24px',
                        borderRadius: '16px',
                        maxWidth: '400px'
                    }
                }}
            >
                <div className={style.errorDialog}>
                    <h3>Error</h3>
                    <p>{errorMessage}</p>
                    <button onClick={() => setErrorDialogOpen(false)}>Close</button>
                </div>
            </Dialog>
        </div>
    );
}

export default Rewards; 